import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowUpRight, ArrowLeft, X, ChevronLeft, ChevronRight } from "lucide-react";
import { setMetaTags } from "@/seo";
import { Reveal, Eyebrow, ArrowButton, ChipFlow, ProjectVisual, EASE } from "@/components/bits";
import { CASE_STUDIES } from "@/data/content";
import { CASE_STUDY_SHOTS } from "@/data/shots";

function Chapter({ index, kicker, title, children, testid }) {
  return (
    <Reveal>
      <section data-testid={testid} className="py-12 lg:py-16 border-t border-tnn-ink/8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <span className="text-xs font-bold text-tnn-purple/50">{String(index).padStart(2, "0")}</span>
            <p className="mt-1 text-[11px] font-bold tracking-[0.2em] uppercase text-tnn-purple">{kicker}</p>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-tnn-ink">{title}</h2>
          </div>
          <div className="lg:col-span-8 space-y-4 text-[15px] leading-relaxed text-tnn-muted">{children}</div>
        </div>
      </section>
    </Reveal>
  );
}

function BulletList({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-2.5">
      {items.map((b, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-tnn-purple shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = CASE_STUDIES[slug];
  const shotsData = CASE_STUDY_SHOTS[slug];
  const flatShots = shotsData ? shotsData.groups.flatMap((g) => g.items) : [];
  const [activeIdx, setActiveIdx] = useState(null);
  const activeShot = activeIdx !== null ? flatShots[activeIdx] : null;

  useEffect(() => {
    if (!cs) return;
    setMetaTags({
      title: `${cs.name} Case Study | The Nutrition Network Africa`,
      description: `${cs.name}: ${cs.tagline}. A TNN Africa case study in nutrition and healthcare innovation.`,
      path: `/our-work/${cs.slug}`,
    });
  }, [cs]);

  useEffect(() => {
    if (activeShot) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    const onKey = (e) => {
      if (activeIdx === null) return;
      if (e.key === "Escape") setActiveIdx(null);
      if (e.key === "ArrowRight") setActiveIdx((i) => (i + 1) % flatShots.length);
      if (e.key === "ArrowLeft") setActiveIdx((i) => (i - 1 + flatShots.length) % flatShots.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [activeShot, activeIdx, flatShots.length]);

  if (!cs) return <Navigate to="/our-work" replace />;

  return (
    <main data-testid={`case-study-${cs.slug}`}>
      <section className="bg-tnn-warm">
        <div className="max-w-6xl mx-auto px-6 pt-32 lg:pt-40 pb-12 lg:pb-16">
          <Reveal>
            <Link
              to="/our-work"
              data-testid="case-study-back"
              className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple hover:text-tnn-deep transition-colors duration-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Our Work
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal delay={0.08}>
                <Eyebrow>Case Study</Eyebrow>
                <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-tnn-ink">{cs.name}</h1>
                <p className="mt-3 text-lg text-tnn-muted">{cs.tagline}</p>
                <a
                  href={cs.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-study-website"
                  className="group mt-6 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.14em] uppercase text-tnn-purple border border-tnn-purple/25 rounded-full px-4 py-2 hover:bg-tnn-purple hover:text-white transition-colors duration-200"
                >
                  {cs.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="rounded-3xl overflow-hidden border border-tnn-ink/8 bg-white">
                  <ProjectVisual project={{ name: cs.name, initial: cs.initial, slug: cs.slug }} large />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-tnn-warm">
        <div className="max-w-6xl mx-auto px-6 pb-4">
          <Chapter index={1} kicker="The Challenge" title="The problem worth solving" testid="cs-challenge">
            {cs.challenge.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <BulletList items={cs.challenge.bullets} />
            {cs.challenge.closing && <p className="font-semibold text-tnn-ink">{cs.challenge.closing}</p>}
          </Chapter>

          <Chapter index={2} kicker="The Solution" title={`What ${cs.name} does`} testid="cs-solution">
            {cs.solution.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <BulletList items={cs.solution.bullets} />
            {cs.solution.closing && <p>{cs.solution.closing}</p>}
          </Chapter>

          <Chapter index={3} kicker="TNN Africa's Role" title="What we brought to it" testid="cs-role">
            <ChipFlow items={cs.role} separator="·" />
            <p>{cs.roleNote}</p>
          </Chapter>

          <Chapter index={4} kicker="What Was Innovative" title="Why it matters" testid="cs-innovation">
            {cs.innovation.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {cs.innovation.flow && <ChipFlow items={cs.innovation.flow} separator="→" />}
            <BulletList items={cs.innovation.bullets} />
            {cs.innovation.closing && <p>{cs.innovation.closing}</p>}
          </Chapter>

          <Chapter index={5} kicker="TNN Africa's Contribution" title="The capability behind the work" testid="cs-contribution">
            <ChipFlow items={cs.contribution} separator="→" />
            <p>
              This is what we bring to every project: clinical depth, turned into products, experiences and real-world
              delivery.
            </p>
          </Chapter>

          <Chapter index={6} kicker="Evidence" title="What exists today" testid="cs-evidence">
            <ul className="space-y-3">
              {cs.evidence.map((e, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-tnn-soft flex items-center justify-center">
                    <Check className="w-3 h-3 text-tnn-purple" />
                  </span>
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </Chapter>
        </div>
      </div>

      {shotsData && (
        <div className="bg-tnn-warm">
          <div className="max-w-6xl mx-auto px-6 pb-16 lg:pb-20">
            <Reveal>
              <section data-testid="cs-product-shots" className="pt-12 lg:pt-16 border-t border-tnn-ink/8">
                <Eyebrow>Inside the Product</Eyebrow>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-tnn-ink">
                  {shotsData.title}
                </h2>
                {shotsData.groups.map((g, gi) => (
                  <div key={gi} className="mt-8">
                    {g.title && (
                      <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-tnn-purple mb-4">{g.title}</p>
                    )}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                      {g.items.map((s, i) => (
                        <figure key={i}>
                          <button
                            type="button"
                            onClick={() => setActiveIdx(flatShots.indexOf(s))}
                            data-testid={`shot-${slug}-${gi}-${i}`}
                            className="group block w-full cursor-zoom-in"
                            aria-label={`Open screenshot: ${s.caption}`}
                          >
                            <img
                              src={s.src}
                              alt={s.caption}
                              loading="lazy"
                              className="w-full aspect-[9/16] object-cover object-top rounded-2xl border border-tnn-ink/8 bg-white shadow-sm transition-shadow duration-300 group-hover:shadow-[0_18px_44px_rgba(40,0,40,0.16)]"
                            />
                          </button>
                          <figcaption className="mt-2.5 text-xs text-tnn-muted leading-relaxed">{s.caption}</figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            </Reveal>
          </div>
        </div>
      )}

      <section className="bg-tnn-warm pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="bg-tnn-deep rounded-[2rem] px-8 py-12 lg:p-16" data-testid="cs-explore">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div>
                  <p className="text-xs font-bold tracking-[0.22em] uppercase text-tnn-lilac">Explore</p>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white max-w-md leading-[1.2]">
                    Interested in building a similar solution?
                  </h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  <ArrowButton to={cs.url} external variant="white" testid="cs-visit-product">
                    Visit {cs.name}
                  </ArrowButton>
                  <ArrowButton to="/contact" variant="outlineLight" testid="cs-partner-cta">
                    Partner with TNN Africa
                  </ArrowButton>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {activeShot && (
          <motion.div
            data-testid="shot-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`Screenshot viewer: ${activeShot.caption}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-tnn-deep/90 backdrop-blur-sm flex flex-col items-center justify-center p-4 sm:p-8"
            onClick={() => setActiveIdx(null)}
          >
            <button
              type="button"
              data-testid="shot-lightbox-close"
              onClick={() => setActiveIdx(null)}
              aria-label="Close screenshot"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white text-tnn-deep flex items-center justify-center shadow-lg hover:bg-tnn-lilac transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
            {flatShots.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid="shot-lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIdx((i) => (i - 1 + flatShots.length) % flatShots.length);
                  }}
                  aria-label="Previous screenshot"
                  className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  data-testid="shot-lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIdx((i) => (i + 1) % flatShots.length);
                  }}
                  aria-label="Next screenshot"
                  className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            <motion.img
              key={activeShot.src}
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.3, ease: EASE }}
              src={activeShot.src}
              alt={activeShot.caption}
              className="max-h-[78vh] w-auto max-w-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-4 text-sm text-white/70 text-center max-w-md">{activeShot.caption}</p>
            <p className="mt-1.5 text-[11px] font-bold tracking-[0.2em] text-white/40" data-testid="shot-lightbox-counter">
              {activeIdx + 1} / {flatShots.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
